import React, { useState } from 'react';
import { Divider, Menu, MenuItem } from '@mui/material';
import WebhookForm from './WebhookForm';
import { useMutateWebhookHandler } from '../dataHandlers/mutateWebhookHandler';
import { useDeleteWebhookHandler } from '../dataHandlers/deleteWebhookHandler';
import ConfirmDialog from './ConfirmDialog';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import TestWebhookModal from './TestWebhookModal';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import {
  ARCHIVE_WEBHOOK_CONFIG,
  CREATE_WEBHOOK_CONFIG,
  READ_WEBHOOK_CONFIG,
  UPDATE_WEBHOOK_CONFIG,
} from '../../../constants/webhookPermissions';

const WebhookActions = ({ selectedWebhook, anchorEl, onClose, isGlobal = false }) => {
  const [openForm, setOpenForm] = useState(false);
  const [openTest, setOpenTest] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [confirmConfig, setConfirmConfig] = useState(null);
  const navigate = useNavigate();
  const { hasWebhookPermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();

  const canCreate = hasWebhookPermission(CREATE_WEBHOOK_CONFIG);
  const canUpdate = hasWebhookPermission(UPDATE_WEBHOOK_CONFIG);
  const canArchive = hasWebhookPermission(ARCHIVE_WEBHOOK_CONFIG);
  const canReadConfig = hasWebhookPermission(READ_WEBHOOK_CONFIG);

  const { mutate: mutateWebhook } = useMutateWebhookHandler({});
  const { deleteWebhook } = useDeleteWebhookHandler({});

  const handleTestEvent = () => {
    if (!canCreate) {
      return;
    }
    setOpenTest(true);
  };

  const handleManageSecret = () => {
    if (!canReadConfig) {
      return;
    }
    navigate(`/webhooks/${selectedWebhook?.id}/secret`, {
      state: { webhookName: selectedWebhook?.name },
    });
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
        <PermissionDeniedWrapper show={!canCreate} title={accessDeniedMessage} fullWidth>
          <MenuItem onClick={handleTestEvent} disabled={!canCreate} sx={{ gap: 1 }}>
            <ElectricBoltIcon color="primary" /> Send Test Event
          </MenuItem>
        </PermissionDeniedWrapper>

        <PermissionDeniedWrapper show={!canReadConfig} title={accessDeniedMessage} fullWidth>
          <MenuItem onClick={handleManageSecret} disabled={!canReadConfig} sx={{ gap: 1 }}>
            <ShieldOutlinedIcon color="primary" /> Manage Signing Secret
          </MenuItem>
        </PermissionDeniedWrapper>

        <Divider />
        <PermissionDeniedWrapper show={!canUpdate} title={accessDeniedMessage} fullWidth>
          <MenuItem
            disabled={!canUpdate}
            onClick={() => {
              if (!canUpdate) {
                return;
              }
              setFormMode('edit');
              setOpenForm(true);
              onClose();
            }}
          >
            Edit
          </MenuItem>
        </PermissionDeniedWrapper>

        <PermissionDeniedWrapper show={!canUpdate} title={accessDeniedMessage} fullWidth>
          <MenuItem
            disabled={!canUpdate}
            onClick={() => {
              if (!canUpdate) {
                return;
              }
              if (selectedWebhook?.active) {
                const deactivateData = {
                  isEdit: true,
                  payload: {
                    ...selectedWebhook,
                    active: false,
                  },
                };
                setConfirmConfig({
                  title: 'Deactivate Webhook',
                  description: 'Are you sure you want to deactivate this webhook? It will stop receiving events.',
                  confirmText: 'Deactivate',
                  confirmColor: '#6366f1',
                  onConfirm: () => {
                    mutateWebhook(deactivateData);
                    setConfirmConfig(null);
                  },
                });
              } else {
                const activateData = {
                  isEdit: true,
                  payload: {
                    ...selectedWebhook,
                    active: true,
                  },
                };
                mutateWebhook(activateData);
              }

              onClose();
            }}
          >
            {selectedWebhook?.active ? 'Deactivate' : 'Activate'}
          </MenuItem>
        </PermissionDeniedWrapper>
        <PermissionDeniedWrapper show={!canArchive} title={accessDeniedMessage} fullWidth>
          <MenuItem
            disabled={!canArchive}
            sx={{ color: '#ef4444' }}
            onClick={() => {
              if (!canArchive) {
                return;
              }
              setConfirmConfig({
                title: 'Delete Webhook',
                description: 'Are you sure you want to delete this webhook? This action cannot be undone.',
                confirmText: 'Delete',
                confirmColor: '#ef4444',
                onConfirm: () => {
                  deleteWebhook({ id: selectedWebhook?.id });
                  setConfirmConfig(null);
                },
              });
              onClose();
            }}
          >
            Delete
          </MenuItem>
        </PermissionDeniedWrapper>
      </Menu>

      <WebhookForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        mode={formMode}
        initialData={selectedWebhook}
        isGlobal={isGlobal}
      />
      <ConfirmDialog
        open={Boolean(confirmConfig)}
        title={confirmConfig?.title}
        description={confirmConfig?.description}
        confirmText={confirmConfig?.confirmText}
        confirmColor={confirmConfig?.confirmColor}
        onConfirm={confirmConfig?.onConfirm}
        onClose={() => setConfirmConfig(null)}
      />

      <TestWebhookModal
        open={openTest}
        onClose={() => setOpenTest(false)}
        webhookId={selectedWebhook?.id}
        tin={!isGlobal ? selectedWebhook?.profile_data[0].tin : null}
        profileId={!isGlobal ? selectedWebhook?.profile_data[0].profile_id : null}
        name={selectedWebhook?.name}
      />
    </>
  );
};

export default WebhookActions;
